import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, Snackbar, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../api/client';
import type { RightCatalogueEntry, RightCategory, RoleInfo } from '../api/types';
import { useAuthStore } from '../stores/auth';
import { Roles } from '../auth/roles';
import { makeDelegationGuard } from '../auth/delegation';

const categoryOrder: RightCategory[] = ['Read', 'Write', 'Edit', 'Delete', 'Other'];

/**
 * Roles admin: edit per-role grants stored in RoleRights.
 *
 * - User / Admin → editable.
 * - ServiceProvider → read-only with explanatory copy. The role short-circuits
 *   in the resolver, so the persisted `*` is decorative.
 *
 * The picker shows every right in the catalogue plus the feature wildcards
 * (`feature.*`) available, sourced from /api/rights.
 */
export function RolesPage() {
  const isSp = useAuthStore((s) => s.isServiceProvider)();
  const hasRole = useAuthStore((s) => s.hasRole);
  const callerRights = useAuthStore((s) => s.user?.effectiveRights);
  const isAdminService = hasRole(Roles.AdminService);
  const unbounded = isSp || isAdminService;

  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [catalogue, setCatalogue] = useState<RightCatalogueEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [editing, setEditing] = useState<RoleInfo | null>(null);
  const [draftRights, setDraftRights] = useState<Set<string>>(new Set());

  // Predicate matching the server's GrantDelegation ceiling — used to grey out
  // rights a bounded admin can't delegate. Unbounded for SP / AdminService.
  const canDelegate = useMemo(
    () => makeDelegationGuard(catalogue, callerRights, unbounded),
    [catalogue, callerRights, unbounded],
  );

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rolesRes, rightsRes] = await Promise.all([
        api.get<{ roles: RoleInfo[] }>('/api/roles/'),
        api.get<{ rights: RightCatalogueEntry[] }>('/api/rights/'),
      ]);
      setRoles(rolesRes.data?.roles ?? []);
      setCatalogue(rightsRes.data?.rights ?? []);
    } catch (ex) {
      setError((ex as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // The picker offers each concrete key plus a `feature.*` wildcard per feature.
  // Selecting feature.* implies every concrete key under it — we model this by
  // hiding the concrete keys when the wildcard is selected.
  const featureKeys = useMemo(() => {
    const set = new Set<string>();
    for (const r of catalogue) set.add(r.featureKey);
    return Array.from(set).sort();
  }, [catalogue]);

  const openEdit = (role: RoleInfo) => {
    if (role.isSystemManaged) return;
    setEditing(role);
    setDraftRights(new Set(role.rights));
  };

  const toggleRight = (key: string) => {
    setDraftRights((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      await api.put(`/api/roles/${encodeURIComponent(editing.name)}/rights`, {
        rights: Array.from(draftRights),
      });
      setSnackbar(`Updated ${editing.name} rights`);
      setEditing(null);
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Roles</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reload}>Refresh</Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit the rights granted to each role. Grants combine with group memberships
            at request time. ServiceProvider and AdminService are system roles — their
            rights are baked into the role definition and any persisted overrides are
            ignored at request time.
          </Typography>
          {!unbounded && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can only add or remove rights you hold yourself. Other rights stay
              checked but locked — the server preserves them on save.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <CircularProgress size={20} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Rights</TableCell>
                  <TableCell width={120} align="right">Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((r) => (
                  <TableRow key={r.name} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
                        {r.isSystemManaged && (
                          <Chip size="small" label="system" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {r.rights.length === 0 ? (
                        <Box component="span" sx={{ color: 'text.disabled' }}>(none — relies on group memberships)</Box>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {r.rights.map((k) => (
                            <Chip key={k} label={k} size="small" sx={{ fontFamily: 'monospace' }} />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={r.isSystemManaged ? 'System role — rights are baked into the role definition and ignored if edited here' : 'Edit rights'}>
                        <span>
                          <Button size="small" startIcon={<EditIcon />}
                                  disabled={r.isSystemManaged}
                                  onClick={() => openEdit(r)}>
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit rights — {editing?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick concrete rights (<code>customers.read.list</code>), category wildcards
            (<code>customers.read.*</code>), or feature wildcards (<code>customers.*</code>).
            Wider grants disable narrower children visually. The global <code>*</code>
            wildcard is reserved for ServiceProvider and rejected by the API.
          </Typography>
          {featureKeys.map((feature) => {
            const featureWildcard = `${feature}.*`;
            const featureSelected = draftRights.has(featureWildcard);
            const entriesInFeature = catalogue.filter((c) => c.featureKey === feature);

            // Group concrete entries by category, preserving the canonical order
            // (Read / Write / Edit / Delete / Other) regardless of catalogue order.
            const byCategory = new Map<RightCategory, RightCatalogueEntry[]>();
            for (const e of entriesInFeature) {
              const list = byCategory.get(e.category) ?? [];
              list.push(e);
              byCategory.set(e.category, list);
            }
            const orderedCategories = categoryOrder.filter((c) => byCategory.has(c));

            const featureDelegable = canDelegate(featureWildcard);
            return (
              <Box key={feature} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 2 }}>{feature}</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox size="small" checked={featureSelected}
                                disabled={!featureDelegable}
                                onChange={() => toggleRight(featureWildcard)} />
                    }
                    label={<code>{featureWildcard}</code>}
                  />
                </Stack>
                <Box sx={{ pl: 3 }}>
                  {orderedCategories.map((category) => {
                    const categoryToken = category.toLowerCase();
                    const categoryWildcard = `${feature}.${categoryToken}.*`;
                    const categoryWildcardSelected = draftRights.has(categoryWildcard);
                    const categoryDelegable = canDelegate(categoryWildcard);
                    const concreteParentDisabled = featureSelected || categoryWildcardSelected;
                    return (
                      <Box key={category} sx={{ mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox size="small"
                                      checked={featureSelected || categoryWildcardSelected}
                                      disabled={featureSelected || !categoryDelegable}
                                      onChange={() => toggleRight(categoryWildcard)} />
                          }
                          label={<code>{categoryWildcard}</code>}
                        />
                        <Box sx={{ pl: 4 }}>
                          {byCategory.get(category)!.map((r) => {
                            const concreteDelegable = canDelegate(r.key);
                            return (
                              <FormControlLabel key={r.key}
                                control={
                                  <Checkbox size="small"
                                            checked={concreteParentDisabled || draftRights.has(r.key)}
                                            disabled={concreteParentDisabled || !concreteDelegable}
                                            onChange={() => toggleRight(r.key)} />
                                }
                                label={
                                  <Box>
                                    <code>{r.key}</code>{' '}
                                    {r.description && (
                                      <Typography component="span" variant="caption" color="text.secondary">
                                        — {r.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
                message={snackbar ?? ''} />
    </Stack>
  );
}
